import React from "react";
import { ASSETS } from "@/lib/assets";
import { Mail } from "lucide-react";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/components/landing/Hero";

function AppleIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.564 12.65c-.023-2.505 2.045-3.712 2.14-3.77-1.17-1.712-2.988-1.945-3.632-1.972-1.547-.156-3.02.912-3.803.912-.783 0-1.994-.888-3.277-.865-1.687.025-3.242.98-4.11 2.49-1.752 3.032-.449 7.523 1.263 9.988.836 1.208 1.833 2.564 3.144 2.516 1.263-.051 1.74-.816 3.267-.816 1.527 0 1.956.816 3.293.79 1.36-.024 2.222-1.231 3.055-2.44.962-1.4 1.359-2.756 1.383-2.826-.03-.014-2.652-1.02-2.723-4.007ZM15.09 5.34c.693-.856 1.166-2.04.036-3.24-1.005 1.086-1.207 2.29-.984 3.24.83.038 1.71-.406 2.4-1.24Z"/>
    </svg>
  );
}

function PlayIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#00D2FF" d="M3.61 1.81a1.98 1.98 0 0 0-.42 1.32v17.74c0 .49.15.94.42 1.32L14 12 3.61 1.81Z"/>
      <path fill="#FFC908" d="m17.11 15.51-2.76-2.76L11.32 12l3.03-2.75 2.76-2.76 3.78 2.15c1.15.65 1.15 2.3 0 2.96l-3.78 1.91Z"/>
      <path fill="#F94336" d="M14.35 12 3.61 22.19c.4.56 1.05.79 1.7.43l12.8-7.26L14.35 12Z"/>
      <path fill="#00E88F" d="M14.35 12 5.31 1.38c-.65-.36-1.3-.13-1.7.43L14.35 12Z"/>
    </svg>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-white border-t border-[rgba(10,10,10,0.08)]"
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <img
              src={ASSETS.logo}
              alt="MamaMatch"
              className="h-10 md:h-12 w-auto object-contain"
            />
            <p className="mt-6 max-w-[380px] text-[15px] leading-[1.55] text-[#6B6660]">
              Rencontre des mamans près de chez toi qui vivent la même étape
              que toi.
            </p>
            <a
              href="mailto:mamamatchoff@gmail.com"
              data-testid="footer-email"
              className="mt-8 inline-flex items-center gap-2 text-[14px] font-medium text-[#0A0A0A] group"
            >
              <Mail className="w-4 h-4 text-[#0A0A0A]/60" strokeWidth={1.6} />
              <span className="mm-underline">mamamatchoff@gmail.com</span>
            </a>
          </div>

          <div className="col-span-6 md:col-span-3">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/50">
              Navigation
            </div>
            <ul className="mt-5 space-y-3 text-[14px]">
              <li>
                <a href="#comment" data-testid="footer-link-how" className="mm-underline text-[#0A0A0A]/80">
                  Fonctionnement
                </a>
              </li>
              <li>
                <a href="#pourquoi" data-testid="footer-link-why" className="mm-underline text-[#0A0A0A]/80">
                  Pourquoi
                </a>
              </li>
              <li>
                <a href="#apercu" data-testid="footer-link-glimpse" className="mm-underline text-[#0A0A0A]/80">
                  Aperçu
                </a>
              </li>
              <li>
                <a href="#faq" data-testid="footer-link-faq" className="mm-underline text-[#0A0A0A]/80">
                  FAQ
                </a>
              </li>
              <li>
                <a href="#download" data-testid="footer-link-download" className="mm-underline text-[#0A0A0A]/80">
                  Télécharger
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-4">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/50">
              Télécharger l'app
            </div>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="badge-app-store"
                className="inline-flex items-center gap-3 rounded-2xl border border-[rgba(10,10,10,0.12)] hover:border-[rgba(10,10,10,0.3)] transition-colors px-4 py-3 max-w-max"
              >
                <AppleIcon className="w-6 h-6" />
                <div className="flex flex-col leading-tight">
                  <span className="text-[10.5px] font-medium text-[#6B6660] uppercase tracking-[0.12em]">
                    Télécharger sur
                  </span>
                  <span className="text-[14px] font-semibold text-[#0A0A0A]">
                    App Store
                  </span>
                </div>
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="badge-play-store"
                className="inline-flex items-center gap-3 rounded-2xl border border-[rgba(10,10,10,0.12)] hover:border-[rgba(10,10,10,0.3)] transition-colors px-4 py-3 max-w-max"
              >
                <PlayIcon className="w-6 h-6" />
                <div className="flex flex-col leading-tight">
                  <span className="text-[10.5px] font-medium text-[#6B6660] uppercase tracking-[0.12em]">
                    Télécharger sur
                  </span>
                  <span className="text-[14px] font-semibold text-[#0A0A0A]">
                    Google Play
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 md:mt-20 pt-8 border-t border-[rgba(10,10,10,0.08)] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <span className="text-[13px] text-[#6B6660]">
            © {new Date().getFullYear()} MamaMatch. Fait avec soin en France.
          </span>
          <div className="flex flex-wrap items-center gap-6 text-[13px] text-[#6B6660]">
            <a
              href="/securite-des-enfants"
              data-testid="footer-child-safety"
              className="mm-underline"
            >
              Sécurité des enfants
            </a>
            <a
              href="#privacy"
              data-testid="footer-privacy"
              className="mm-underline"
            >
              Politique de confidentialité
            </a>
            <a
              href="#terms"
              data-testid="footer-terms"
              className="mm-underline"
            >
              Conditions d'utilisation
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
